import type {DefinitionOrReference} from '@github-ui/code-nav'
import {DuplicateOnKeydownButton} from '@github-ui/code-view-shared/components/DuplicateOnKeydownButton'
import {useFilesPageInfo, useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {useShortcut} from '@github-ui/code-view-shared/hooks/shortcuts'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import {linkButtonSx} from '@github-ui/code-view-shared/utilities/styles'
import type {PanelType} from '@github-ui/code-view-types'
import type {FileReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import CopilotChatButton from '@github-ui/copilot-code-chat/CopilotCodeLinesMenu'
import type {CopilotInfo} from '@github-ui/copilot-common-types'
import {CopilotPopover} from '@github-ui/copilot-popover'
import {type BannerProps, SpacesSelectionMenu} from '@github-ui/copilot-spaces/components/SpacesSelectionMenu'
import {useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {NewAgentTaskButton} from '@github-ui/new-agent-task-button/NewAgentTaskButton'
import {Link} from '@github-ui/react-core/link'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ScreenReaderHeading} from '@github-ui/screen-reader-heading'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useClientValue} from '@github-ui/use-client-value'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useNavigate} from '@github-ui/use-navigate'
import {
  CodeSquareIcon,
  CopyIcon,
  DownloadIcon,
  KebabHorizontalIcon,
  PencilIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import type {ButtonProps} from '@primer/react'
import {ActionList, ActionMenu, ButtonGroup, Link as PrimerLink} from '@primer/react'
import {Box, IconButton, LinkButton} from '@primer/styled-react'
import {clsx} from 'clsx'
import {useMemo, useRef} from 'react'

// eslint-disable-next-line no-restricted-imports
import {copyText} from '../../../github/command-palette/copy'
import {useAlertTooltip} from '../../../react-shared/hooks/use-alert-tooltip'
import {useFindInFileOpen} from '../../contexts/FindInFileOpenContext'
import {useCurrentBlame} from '../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../hooks/CurrentBlob'
import {BlobDisplayType, useBlobRendererType} from '../../hooks/use-blob-renderer-type'
import {useCopyRawBlobContents} from '../../hooks/use-copy-raw-blob-contents'
import {setKeyboardNavUsed} from '../../hooks/use-cursor-navigation'
import {trackStickyHeader, useStickyHeaderHeight} from '../../hooks/use-sticky-header-height'
import {useStickyObserver} from '../../hooks/use-sticky-observer'
import {updateExpandPreferences} from '../../hooks/use-update-panel-expand-preferences'
import {type CopyState, getCopyStateUI} from '../../utilities/Copy'
import {textAreaId} from '../../utilities/lines'
import AccessibleIconButton from '../AccessibleIconButton'
import type {CodeNavData} from '../blob/BlobContent/BlobContent'
import type {CodeLineData} from '../blob/BlobContent/Code/hooks/use-code-lines'
import FindInFilePopover from '../blob/BlobContent/CodeNav/FindInFilePopover'
import {ContributorAvatars} from '../Contributors'
import styles from './BlobViewHeader.module.css'
import FileNameStickyHeader from './FileNameStickyHeader'
import BlameAgeLegend from './header-components/BlameAgeLegend'
import BlobTabButtons from './header-components/BlobTabButtons'
import CodeSizeDetails from './header-components/CodeSizeDetails'
import EditMenuActionItems from './header-components/EditMenuActionItems'
import {qualifyRef} from './header-components/NavigationMenu'
import {useHasOpenWithItems} from './header-components/OpenWithActionItems'
import RawMenuActionItems, {downloadFile} from './header-components/RawMenuActionItems'
import TableOfContents from './header-components/TableOfContents'
import {StickyLinesHeader} from './StickyLinesHeader'

export default function BlobViewHeader({
  openPanel,
  setOpenPanel,
  showTree,
  validCodeNav,
  treeToggleElement,
  searchTerm,
  setSearchTerm,
  currentStickyLines,
  focusedSearchResult,
  setFocusedSearchResult,
  searchResults,
  searchingText,
  stickyHeaderRef,
  copilotInfo,
  colorizedLines,
  onBannerChange,
}: {
  openPanel: PanelType | undefined
  setOpenPanel: (panel: PanelType | undefined) => void
  showTree: boolean
  validCodeNav: boolean
  treeToggleElement: React.ReactElement
  currentStickyLines: Map<number, CodeLineData>
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  focusedSearchResult: number | undefined
  setFocusedSearchResult: (idx: number | undefined) => void
  searchResults: DefinitionOrReference[]
  searchingText: CodeNavData
  stickyHeaderRef: React.RefObject<HTMLDivElement | null>
  copilotInfo?: CopilotInfo
  colorizedLines?: SafeHTMLString[] | null
  onBannerChange?: (bannerProps: BannerProps | null) => void
}) {
  const payload = useCurrentBlob()
  const symbolsEnabled = payload.symbolsEnabled
  const isStickied = useStickyObserver(stickyHeaderRef)
  const {copilotAccessAllowed, copilotSpacesEnabled, refInfo, path} = useFilesPageInfo()
  const displayType = useBlobRendererType()
  const {sendRepoClickEvent} = useReposAnalytics()
  const stickyHeaderHeight = useStickyHeaderHeight()
  const {copyFilePathShortcut} = useShortcut()
  const {copyPermalinkShortcut} = useShortcut()
  const repository = useCurrentRepository()
  const showAgentTaskButton = isFeatureEnabled('copilot_agent_tasks_btn_code_view') && payload.copilotSWEAgentEnabled
  const copilotSpacesCodeView = isFeatureEnabled('copilot_spaces_code_view')

  const {githubDevUrl} = useReposAppPayload()
  const {
    headerInfo: {toc, onBranch, ghDesktopPath},
    viewable,
  } = payload
  const hasOpenWithItem = useHasOpenWithItems(githubDevUrl, onBranch, ghDesktopPath)

  const blame = useCurrentBlame()

  const onCopy = useCopyRawBlobContents()
  const {createPermalink} = useUrlCreator()
  const {addToast} = useToastContext()

  const {findInFileOpen, setFindInFileOpen} = useFindInFileOpen()
  const rawActionsButtonRef = useRef(null)
  const [updateTooltipMessage, clearTooltipMessage, portalTooltip] = useAlertTooltip(
    'raw-actions-message-tooltip',
    rawActionsButtonRef,
    {direction: 'nw'},
  )

  const {getUrl} = useUrlCreator()
  const blobUrl = getUrl()
  const [fileUrl] = useClientValue(() => window.location.origin + blobUrl, blobUrl)
  const fileReference: FileReference = useMemo(
    () => ({
      type: 'file',
      url: fileUrl,
      path,
      repoID: repository.id,
      repoOwner: repository.ownerLogin,
      repoName: repository.name,
      ref: qualifyRef(refInfo.name, refInfo.refType ?? 'branch'),
      commitOID: refInfo.currentOid,
    }),
    [
      fileUrl,
      path,
      repository.id,
      repository.ownerLogin,
      repository.name,
      refInfo.name,
      refInfo.refType,
      refInfo.currentOid,
    ],
  )

  const editAllowedOnDefaultBranch = viewable && refInfo.canEditOnDefaultBranch

  return (
    <>
      <div ref={trackStickyHeader} className={styles.Box}>
        <div className="react-blob-sticky-header">
          <FileNameStickyHeader isStickied={isStickied} showTree={showTree} treeToggleElement={treeToggleElement} />
        </div>
        <Box
          sx={{
            borderRadius: isStickied ? '0px' : '6px 6px 0px 0px',
          }}
          className={styles.Box_1}
        >
          <ScreenReaderHeading as="h2" text="File metadata and controls" />
          <div className={styles.Box_2}>
            <BlobTabButtons />
            <CodeSizeDetails className="react-code-size-details-in-header" />
            <CopilotPopover
              copilotInfo={copilotInfo}
              className="react-code-size-details-in-header"
              view={blame ? 'blame' : 'preview'}
            />
          </div>

          <div className={styles.Box_3}>
            {/* Copy link and permalink shortcut */}
            {copyFilePathShortcut.hotkey && (
              <KeyboardShortcut
                hotkey={copyFilePathShortcut.hotkey}
                onActivate={() => {
                  copyText(path)
                  // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                  addToast({
                    type: 'success',
                    message: 'Path copied!',
                  })
                }}
              />
            )}
            {copyPermalinkShortcut.hotkey && (
              <KeyboardShortcut
                hotkey={copyPermalinkShortcut.hotkey}
                onActivate={() => {
                  copyText(createPermalink({absolute: true}))
                  // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                  addToast({
                    type: 'success',
                    message: 'Permalink copied!',
                  })
                }}
              />
            )}

            {copilotAccessAllowed && copilotSpacesCodeView && copilotSpacesEnabled && (
              <SpacesSelectionMenu
                repository={repository}
                currentFile={fileReference}
                onBannerChange={onBannerChange}
              />
            )}

            <CopilotChatButton
              copilotAccessAllowed={copilotAccessAllowed}
              messageReference={fileReference}
              hideDropdown
              id="blob-view-header-copilot-icon"
            />

            {showAgentTaskButton && (
              <NewAgentTaskButton
                isIconButton
                size="small"
                tooltipDirection="n"
                currentRepo={repository}
                currentReference={{
                  path,
                }}
                hideRepoSelector
                eventType="new_agent_task_button_codeview"
              />
            )}

            <div className={clsx('react-blob-header-edit-and-raw-actions', styles.Box_4)}>
              <RawGroup onCopy={onCopy} fileName={payload.displayName} />
              <EditMenu />
            </div>
            {displayType === BlobDisplayType.Code && !blame && validCodeNav && symbolsEnabled && (
              <SymbolsButton
                isCodeNavOpen={openPanel === 'codeNav'}
                setCodeNavOpen={open => {
                  if (open) {
                    sendRepoClickEvent('BLOB_SYMBOLS_MENU.OPEN')
                  }
                  localStorage.setItem('codeNavOpen', open ? 'codeNav' : '')
                  updateExpandPreferences(null, open, null)
                  setOpenPanel(open ? 'codeNav' : undefined)
                }}
                size="small"
                searchingText={searchingText.selectedText}
              />
            )}
            {!blame && <TableOfContents toc={toc} openPanel={openPanel} setOpenPanel={setOpenPanel} />}

            <div className="react-blob-header-edit-and-raw-actions-combined">
              {portalTooltip}
              <ActionMenu anchorRef={rawActionsButtonRef}>
                <ActionMenu.Anchor>
                  <IconButton
                    icon={KebabHorizontalIcon}
                    tooltipDirection="nw"
                    aria-label="Edit and raw actions"
                    className={clsx('js-blob-dropdown-click', styles.IconButton)}
                    size={'small'}
                    title="More file actions"
                    variant={'invisible'}
                    data-testid="more-file-actions-button"
                    onBlur={clearTooltipMessage}
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay
                  className={clsx('react-blob-header-edit-and-raw-actions-combined', styles.ActionMenu_Overlay)}
                  width="small"
                >
                  <ActionList>
                    {((refInfo.canEdit && viewable) || editAllowedOnDefaultBranch || hasOpenWithItem) && (
                      <div className="react-navigation-menu-edit-and-raw-actions">
                        <EditMenuActionItems
                          editAllowed={!!(viewable && refInfo.canEdit) || !!editAllowedOnDefaultBranch}
                          hasOpenWithItem={hasOpenWithItem}
                        />
                        <ActionList.Divider />
                      </div>
                    )}
                    <>
                      <RawMenuActionItems
                        viewable={viewable}
                        onCopy={onCopy}
                        name={payload.displayName}
                        updateTooltipMessage={updateTooltipMessage}
                        all
                      />
                    </>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </div>
          </div>
        </Box>
        {blame && (
          <div className={styles.Box_5}>
            <BlameAgeLegend />
            <ContributorAvatars />
          </div>
        )}
      </div>
      {findInFileOpen && (
        <FindInFilePopover
          stickied={isStickied}
          searchTerm={searchTerm}
          focusedSearchResult={focusedSearchResult}
          setFocusedSearchResult={setFocusedSearchResult}
          setSearchTerm={setSearchTerm}
          searchResults={searchResults}
          onClose={() => {
            setFindInFileOpen(false)
            if (openPanel === 'codeNav') {
              setSearchTerm(searchingText.selectedText)
            }
          }}
        />
      )}
      <div>
        {!blame && currentStickyLines.size > 0 && (
          <Box
            sx={{
              top: stickyHeaderHeight,
            }}
            className={styles.Box_6}
          >
            <StickyLinesHeader currentStickyLines={currentStickyLines} colorizedLines={colorizedLines} />
          </Box>
        )}
      </div>
    </>
  )
}

function EditMenu() {
  const payload = useCurrentBlob()
  const {getUrl} = useUrlCreator()
  const {refInfo} = useFilesPageInfo()
  const {canEdit} = refInfo
  const {sendRepoClickEvent} = useReposAnalytics()
  const {githubDevUrl} = useReposAppPayload()
  const navigate = useNavigate()
  const {editFileShortcut, openWithGitHubDevShortcut, openWithGitHubDevInNewWindowShortcut} = useShortcut()
  const editAllowedOnDefaultBranch = !!refInfo.canEditOnDefaultBranch

  const {
    headerInfo: {editTooltip, ghDesktopPath, onBranch},
  } = payload

  const hasOpenWithItem = useHasOpenWithItems(githubDevUrl, onBranch, ghDesktopPath)
  if (!canEdit && !hasOpenWithItem) {
    return null
  }
  const onEditClick = () => {
    navigate(getUrl({action: 'edit'}))
  }
  const openGitHubDevInNewWindow = () => {
    window.open(githubDevUrl, '_blank')
  }
  const openInGitHubDev = () => {
    //need the pathname because the githubDevUrl is just the root, and cut off the first '/'
    navigate(githubDevUrl + window.location.pathname.substring(1))
  }

  return (
    <>
      {githubDevUrl && (
        <>
          <PrimerLink
            className="js-github-dev-shortcut d-none"
            data-hotkey={openWithGitHubDevShortcut.hotkey}
            href={githubDevUrl}
          />
          <DuplicateOnKeydownButton
            buttonFocusId={textAreaId}
            buttonHotkey={openWithGitHubDevShortcut.hotkey}
            onlyAddHotkeyScopeButton
            onButtonClick={openInGitHubDev}
          />
          <PrimerLink
            className="js-github-dev-new-tab-shortcut d-none"
            data-hotkey={openWithGitHubDevInNewWindowShortcut.hotkey}
            href={githubDevUrl}
            target="_blank"
          />
          <DuplicateOnKeydownButton
            buttonFocusId={textAreaId}
            buttonHotkey={openWithGitHubDevInNewWindowShortcut.hotkey}
            onlyAddHotkeyScopeButton
            onButtonClick={openGitHubDevInNewWindow}
          />
        </>
      )}
      <ButtonGroup>
        {canEdit ? (
          <IconButton
            as={Link}
            tooltipDirection="nw"
            aria-label={editTooltip}
            data-hotkey={editFileShortcut.hotkey}
            icon={PencilIcon}
            to={getUrl({action: 'edit'})}
            size="small"
            sx={{
              ...linkButtonSx,
            }}
            data-testid="edit-button"
          />
        ) : (
          <AccessibleIconButton icon={PencilIcon} aria-label={editTooltip} disabled />
        )}

        <ActionMenu onOpenChange={open => open && sendRepoClickEvent('BLOB_EDIT_DROPDOWN')}>
          <ActionMenu.Anchor>
            <IconButton
              tooltipDirection="nw"
              icon={TriangleDownIcon}
              size="small"
              aria-label="More edit options"
              data-testid="more-edit-button"
            />
          </ActionMenu.Anchor>

          <ActionMenu.Overlay align="end">
            <ActionList>
              <EditMenuActionItems
                editAllowed={canEdit || editAllowedOnDefaultBranch}
                hasOpenWithItem={hasOpenWithItem}
              />
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </ButtonGroup>
      {canEdit && (
        <DuplicateOnKeydownButton
          buttonFocusId={textAreaId}
          buttonHotkey={'e,Shift+E'}
          onlyAddHotkeyScopeButton
          onButtonClick={onEditClick}
        />
      )}
    </>
  )
}

function RawGroup({onCopy, fileName}: {onCopy: () => Promise<CopyState>; fileName: string}) {
  const {viewRawContentShortcut, copyRawContentShortcut, downloadRawContentShortcut} = useShortcut()
  const {
    renderImageOrRaw,
    renderedFileInfo,
    viewable,
    image,
    rawBlobUrl,
    headerInfo: {isGitLfs: isLfs},
  } = useCurrentBlob()
  const navigate = useNavigate()
  const {addToast} = useToastContext()
  const copyButtonRef = useRef(null)
  const [updateTooltipMessage, clearTooltipMessage, portalTooltip] = useAlertTooltip(
    'raw-copy-message-tooltip',
    copyButtonRef,
  )
  const isOnlyPreviewable = !isLfs && ((renderedFileInfo && !viewable) || image)

  const downloadRawFile = async () => {
    await downloadFile(rawBlobUrl, fileName)
  }
  const navToRaw = () => navigate(rawBlobUrl)
  const copyFile = async () => {
    const result = await onCopy()
    const {ariaLabel} = getCopyStateUI(result)
    updateTooltipMessage(ariaLabel)
  }

  const lfsDownloadUrl = new URL(rawBlobUrl, ssrSafeLocation.origin)
  lfsDownloadUrl.searchParams.set('download', '')
  const downloadButtonProps = {
    ['aria-label']: 'Download raw file',
    ['tooltipDirection']: 'n',
    icon: DownloadIcon,
    size: 'small',
    onClick: async () => {
      if (!isLfs) {
        await downloadFile(rawBlobUrl, fileName)
      }
    },
    ['data-testid']: 'download-raw-button',
    ['data-hotkey']: downloadRawContentShortcut.hotkey,
    // wrapping in tooltip breaks button group styles, manually override borders for now
    sx: {borderTopLeftRadius: 0, borderBottomLeftRadius: 0},
  } as const

  return isOnlyPreviewable ? (
    <>
      <IconButton
        aria-label="Download raw file"
        tooltipDirection="n"
        icon={DownloadIcon}
        size="small"
        onClick={downloadRawFile}
        data-testid="download-raw-button"
        data-hotkey={downloadRawContentShortcut.hotkey}
      />
      <DuplicateOnKeydownButton
        buttonTestLabel="download-raw-button-shortcut"
        buttonFocusId={textAreaId}
        buttonHotkey={downloadRawContentShortcut.hotkey}
        onlyAddHotkeyScopeButton
        onButtonClick={downloadRawFile}
      />
    </>
  ) : (
    <>
      <ButtonGroup>
        <LinkButton
          href={rawBlobUrl}
          download={renderImageOrRaw ? 'true' : undefined}
          size="small"
          sx={{
            linkButtonSx,
          }}
          data-testid="raw-button"
          data-hotkey={viewRawContentShortcut.hotkey}
          className={styles.LinkButton}
        >
          Raw
        </LinkButton>
        {!isLfs && (
          <>
            <IconButton
              ref={copyButtonRef}
              aria-label="Copy raw file"
              tooltipDirection="n"
              icon={CopyIcon}
              size="small"
              onMouseLeave={clearTooltipMessage}
              onClick={copyFile}
              data-testid="copy-raw-button"
              data-hotkey={copyRawContentShortcut.hotkey}
              onBlur={clearTooltipMessage}
            />
            {portalTooltip}
          </>
        )}
        {isLfs ? (
          <IconButton as="a" data-turbo="false" href={lfsDownloadUrl.toString()} {...downloadButtonProps} />
        ) : (
          <IconButton {...downloadButtonProps} />
        )}
      </ButtonGroup>
      <DuplicateOnKeydownButton
        buttonFocusId={textAreaId}
        buttonHotkey={viewRawContentShortcut.hotkey}
        buttonTestLabel="raw-button-shortcut"
        onlyAddHotkeyScopeButton
        onButtonClick={navToRaw}
      />
      {!isLfs && (
        <>
          <DuplicateOnKeydownButton
            buttonTestLabel="copy-raw-button-shortcut"
            buttonFocusId={textAreaId}
            buttonHotkey={copyRawContentShortcut.hotkey}
            onlyAddHotkeyScopeButton
            onButtonClick={async () => {
              const result = await onCopy()
              const {ariaLabel, content} = getCopyStateUI(result)
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                message: ariaLabel,
                icon: content,
              })
            }}
          />
          <DuplicateOnKeydownButton
            buttonTestLabel="download-raw-button-shortcut"
            buttonFocusId={textAreaId}
            buttonHotkey={downloadRawContentShortcut.hotkey}
            onlyAddHotkeyScopeButton
            onButtonClick={async () => {
              await downloadFile(rawBlobUrl, fileName)
            }}
          />
        </>
      )}
    </>
  )
}

function SymbolsButton({
  isCodeNavOpen,
  setCodeNavOpen,
  size,
  searchingText,
}: {
  isCodeNavOpen: boolean
  setCodeNavOpen: (isOpen: boolean) => void
  size?: ButtonProps['size']
  searchingText: string
}) {
  const {toggleSymbolsShortcut} = useShortcut()
  const shouldShowIndicator = !useCodeViewOptions().openSymbolsOption.enabled && !isCodeNavOpen
  const toggleCodeNav = () => {
    setKeyboardNavUsed(true)
    setCodeNavOpen(!isCodeNavOpen)
  }

  return (
    <IconButton
      aria-label={isCodeNavOpen ? 'Close symbols panel' : 'Open symbols panel'}
      tooltipDirection="nw"
      aria-pressed={isCodeNavOpen}
      aria-expanded={isCodeNavOpen}
      aria-controls="symbols-pane"
      icon={CodeSquareIcon}
      className={clsx(shouldShowIndicator && searchingText ? 'react-button-with-indicator' : '', styles.IconButton_2)}
      data-hotkey={toggleSymbolsShortcut.hotkey}
      onClick={toggleCodeNav}
      variant="invisible"
      data-testid="symbols-button"
      id="symbols-button"
      size={size}
    />
  )
}

function KeyboardShortcut({hotkey, onActivate}: {hotkey: string; onActivate: () => void}) {
  return <DuplicateOnKeydownButton buttonFocusId={textAreaId} buttonHotkey={hotkey} onButtonClick={onActivate} />
}

try{ BlobViewHeader.displayName ||= 'BlobViewHeader' } catch {}
try{ EditMenu.displayName ||= 'EditMenu' } catch {}
try{ RawGroup.displayName ||= 'RawGroup' } catch {}
try{ SymbolsButton.displayName ||= 'SymbolsButton' } catch {}
try{ KeyboardShortcut.displayName ||= 'KeyboardShortcut' } catch {}